export const isLocalEnvironment = process.env.NODE_ENV === "development";

export const envPrefix: string = "#{ENV_PREFIX}";

const trimmedPrefix = envPrefix.toString().trim();

export let environment = isLocalEnvironment
  ? "qa."
  : trimmedPrefix && !["prd", "prod"].includes(trimmedPrefix.toLowerCase())
  ? `${trimmedPrefix}.`
  : "";

environment = environment !== "." ? environment : "";

export const isProduction = environment === "";

const ENABLE_ATTESTATION_LOCAL = true;
const ENABLE_ATTESTATION_NON_PRODUCTION = true;
const ENABLE_ATTESTATION_PRODUCTION = true;

/**
 * Determines whether attestation checks should be on or off
 * @returns A boolean based on the above flags and the current environment
 */
function determineAttestation() {
  if (isLocalEnvironment) return ENABLE_ATTESTATION_LOCAL;
  if (isProduction) return ENABLE_ATTESTATION_PRODUCTION;
  return ENABLE_ATTESTATION_NON_PRODUCTION;
}

export const ENABLE_ATTESTATION = determineAttestation();

/* Amplitude Experiment deployment keys per environment.
 * Client-side public keys — they ship in the browser bundle, so committing them is fine.
 * Source: Amplitude > Experiment > Deployments > <deployment> > Deployment API Key.
 * Naming convention: members-portal-{qa,stg,prd}. */
const amplitudeDeploymentKeys = {
  qa: "client-VHHRfFS2UQ3Lfke7W79adp98OHpPBYWJ",
  stg: "client-Fg8NXyq6EHKiYdK34Pv8pS4t41UUnffZ",
  prd: "client-k7lLcQiSYu9QP5IFVOL7o3D2ci8RvsPM",
} as const;

/* The ENV_PREFIX values that mean production. "prd" is what the stg/prd workflow
 * actually deploys with (.github/workflows/ph-member-portal-stg-prd.yml sets ENV: prd);
 * "" only occurs when the token was never replaced. Both are treated as production,
 * matching how `environment` above collapses them to the bare hostname. */
const productionPrefixes = ["", "prd", "prod"];

/**
 * Maps the deploy-time ENV_PREFIX token to its Amplitude deployment key.
 *
 * Production deploys as ENV_PREFIX=prd, not as an empty string. This previously
 * matched only the empty case, so production fell through to "" -- no deployment key,
 * ENABLE_AMPLITUDE false, and every feature flag resolving to disabled no matter how
 * it was configured in Amplitude (PPI-1836: FlexBenefits was invisible in production
 * while working in QA, where ENV_PREFIX=qa matched). `environment` already handled
 * "prd"/"prod", which is why the API hostname was right the whole time and the
 * mismatch stayed hidden.
 *
 * Exported for tests: the prefix is a build-time literal in this module, so calling
 * this directly is the only way to exercise a genuinely deployed value.
 */
export function resolveAmplitudeDeploymentKey(prefix: string): string {
  const normalized = prefix.toString().trim().toLowerCase();
  if (normalized === "qa") return amplitudeDeploymentKeys.qa;
  if (normalized === "stg") return amplitudeDeploymentKeys.stg;
  if (productionPrefixes.includes(normalized))
    return amplitudeDeploymentKeys.prd;
  return "";
}

function getAmplitudeDeploymentKey(): string {
  if (isLocalEnvironment) return amplitudeDeploymentKeys.qa;
  return resolveAmplitudeDeploymentKey(envPrefix);
}

export const AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY = getAmplitudeDeploymentKey();

export const ENABLE_AMPLITUDE = isLocalEnvironment
  ? process.env.REACT_APP_ENABLE_AMPLITUDE === "true"
  : /* istanbul ignore next — deployed-env branch, covered post-replacement */
    Boolean(AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY);
